import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useCurrent, useTenantPermissions } from '@maxtropy/components';
import Cookies from 'js-cookie';

import {
  assistantButtonId,
  assistantIframeId,
  assistantScriptId,
  centerAssistantIframe,
  clampAssistantPosition,
  defaultAssistantSize,
  findAssistantElement,
  getAssistantAnchorLayout,
  getAssistantDocuments,
  getAssistantScriptDocument,
  getAssistantWindows,
  getScaleValue,
  installAssistantButtonHideStyle,
  localAssistantOrigin,
  resetAssistantButtonStyle,
  resetAssistantIframeStyle,
  shouldInjectAssistantScript,
  syncAssistantPortalContainer,
} from './util';
import type {
  AssistantOffset,
  AssistantPlacement,
  AssistantPosition,
  AssistantSize,
  AssistantTransitionEffect,
} from './util';

type FloatingAssistantPositionOptions = {
  /** 用于测量初始位置的 DOM 锚点 */
  anchorRef: React.RefObject<HTMLElement>;
  /** 助手相对锚点的定位方式 */
  placement?: AssistantPlacement;
  /** 设计稿尺寸，会按 pageScale 缩放 */
  baseSize?: AssistantSize;
  /** 相对锚点的偏移量，会按 pageScale 缩放 */
  offset?: AssistantOffset;
  /** 页面整体缩放比例 */
  pageScale?: number;
  /** 关闭时清空定位，避免隐藏状态继续响应布局变化 */
  enabled?: boolean;
  /** 拖拽后进入 pinned，后续只 clamp 不再跟随锚点跳回初始位置 */
  pinned: boolean;
};

/**
 * 管理助手脚本、默认按钮隐藏、iframe 样式同步和配置 postMessage。
 * 这个 hook 只负责外部助手 embed 生命周期，不处理本地机器人按钮的拖拽和渲染。
 */
export const useAssistantEmbed = (
  buttonHostRef: React.RefObject<HTMLDivElement>,
  pageScale?: number,
  enabled = true,
  openEffect?: AssistantTransitionEffect,
  closeEffect?: AssistantTransitionEffect
) => {
  const current = useCurrent();
  const permissions = useTenantPermissions();
  const hasPermission = permissions?.includes('b-aiAgent');
  const staffId = current?.staff?.id;
  const tenantMcid = current?.tenant?.mcid;
  const assistantOrigin =
    window.ASSISTANT_ORIGIN ||
    process.env.REACT_APP_ASSISTANT_ORIGIN ||
    (process.env.NODE_ENV === 'development' ? localAssistantOrigin : '');
  const showChat = hasPermission || process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (!enabled) return;

    const uninstallAssistantButtonHideStyle = installAssistantButtonHideStyle();
    const assistantWindows = getAssistantWindows();
    const assistantDocuments = getAssistantDocuments();
    const previousAssistantConfigs = assistantWindows.map(assistantWindow => ({
      assistantWindow,
      config: assistantWindow.assistantConfig,
    }));
    const transitionCleanups = new Map<AssistantEmbedController, () => void>();

    const syncAssistantTransitions = () => {
      if (!openEffect && !closeEffect) return;

      assistantWindows.forEach(assistantWindow => {
        const controller = assistantWindow.assistantEmbed;
        if (!controller || transitionCleanups.has(controller)) return;
        transitionCleanups.set(controller, controller.registerTransitions({ open: openEffect, close: closeEffect }));
      });
    };

    syncAssistantTransitions();

    if (!staffId || !tenantMcid || !assistantOrigin) {
      return () => {
        transitionCleanups.forEach(cleanup => cleanup());
        uninstallAssistantButtonHideStyle();
      };
    }

    const nextConfig = {
      staffId,
      tenantMcid,
      baseUrl: assistantOrigin,
      showChat,
      GT: Cookies.get('GT'),
      dynamicScript: false,
      hasHistory: false,
    };

    const syncAssistantConfig = () => {
      assistantWindows.forEach(assistantWindow => {
        assistantWindow.assistantConfig = nextConfig;
      });
    };

    syncAssistantConfig();

    const syncIframePosition = (transform = `translate(-50%, -50%) scale(${getScaleValue(pageScale)})`) => {
      const iframe = findAssistantElement<HTMLIFrameElement>(assistantIframeId);
      if (!iframe) return;
      centerAssistantIframe(iframe, pageScale, transform);
    };

    const syncButtonPosition = () => {
      const host = buttonHostRef.current;
      const button = findAssistantElement<HTMLDivElement>(assistantButtonId);
      if (!host || !button) return;

      const rect = host.getBoundingClientRect();
      button.style.setProperty(`--${assistantButtonId}-left`, `${rect.left}px`);
      button.style.setProperty(`--${assistantButtonId}-top`, `${rect.top}px`);
      button.style.setProperty(`--${assistantButtonId}-right`, 'unset');
      button.style.setProperty(`--${assistantButtonId}-bottom`, 'unset');
      button.style.setProperty(`--${assistantButtonId}-width`, `${rect.width}px`);
      button.style.setProperty(`--${assistantButtonId}-height`, `${rect.height}px`);
      button.style.setProperty('display', 'none', 'important');
      button.style.setProperty('opacity', '0', 'important');
      button.style.setProperty('visibility', 'hidden', 'important');
      button.style.setProperty('pointer-events', 'none', 'important');
      button.style.setProperty('z-index', '0', 'important');
    };

    const syncAssistantLayout = () => {
      syncAssistantConfig();
      syncAssistantTransitions();
      syncAssistantPortalContainer();
      syncButtonPosition();
      syncIframePosition();
    };

    const postConfigToIframe = (target?: Window | null) => {
      const payload = { type: 'initConfig', payload: nextConfig };
      if (target) {
        target.postMessage(payload, '*');
        return;
      }
      const iframe = findAssistantElement<HTMLIFrameElement>(assistantIframeId);
      iframe?.contentWindow?.postMessage(payload, '*');
    };

    let acked = false;
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'assistant-config-request') {
        postConfigToIframe(event.source as Window);
      }
      if (event.data?.type === 'assistant-config-received') {
        acked = true;
      }
    };

    assistantWindows.forEach(assistantWindow => assistantWindow.addEventListener('message', onMessage, true));

    const timer = window.setInterval(() => {
      syncAssistantLayout();
      if (acked) return;
      postConfigToIframe();
    }, 1000);

    const resizeObserver = new ResizeObserver(syncAssistantLayout);
    if (buttonHostRef.current) {
      resizeObserver.observe(buttonHostRef.current);
    }
    const mutationObserver = new MutationObserver(syncAssistantLayout);
    assistantDocuments.forEach(assistantDocument => {
      mutationObserver.observe(assistantDocument.body, { childList: true });
      const fullscreenBox = assistantDocument.getElementById('full_screen_box');
      if (fullscreenBox) {
        mutationObserver.observe(fullscreenBox, { childList: true });
      }
    });
    window.addEventListener('resize', syncAssistantLayout);
    document.addEventListener('fullscreenchange', syncAssistantLayout);

    let script = findAssistantElement<HTMLScriptElement>(assistantScriptId);
    if (!script) {
      if (shouldInjectAssistantScript()) {
        const scriptDocument = getAssistantScriptDocument();
        script = scriptDocument.createElement('script');
        script.id = assistantScriptId;
        script.src = `${assistantOrigin}/embed.js`;
        script.async = true;
        script.onload = () => {
          postConfigToIframe();
          syncAssistantLayout();
        };
        scriptDocument.body.appendChild(script);
      }
    } else {
      postConfigToIframe();
      syncAssistantLayout();
    }

    return () => {
      transitionCleanups.forEach(cleanup => cleanup());
      assistantWindows.forEach(assistantWindow => assistantWindow.removeEventListener('message', onMessage, true));
      window.removeEventListener('resize', syncAssistantLayout);
      document.removeEventListener('fullscreenchange', syncAssistantLayout);
      window.clearInterval(timer);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      resetAssistantButtonStyle();
      resetAssistantIframeStyle();
      previousAssistantConfigs.forEach(({ assistantWindow, config }) => {
        assistantWindow.assistantConfig = config;
      });
      uninstallAssistantButtonHideStyle();
    };
  }, [assistantOrigin, buttonHostRef, closeEffect, enabled, openEffect, pageScale, showChat, staffId, tenantMcid]);
};

/**
 * 根据外部锚点持续计算本地机器人入口的位置和尺寸。
 * 未拖拽时跟随锚点刷新；拖拽后保持用户放置的位置，仅在缩放或窗口变化时限制到可视区。
 */
export const useFloatingAssistantPosition = ({
  anchorRef,
  placement,
  baseSize = defaultAssistantSize,
  offset,
  pageScale,
  enabled,
  pinned,
}: FloatingAssistantPositionOptions) => {
  const [assistantPosition, setAssistantPosition] = useState<AssistantPosition | null>(null);
  const [assistantSize, setAssistantSize] = useState<AssistantSize | null>(null);

  useLayoutEffect(() => {
    if (!enabled) {
      setAssistantPosition(null);
      setAssistantSize(null);
      return;
    }

    const syncAssistantPosition = () => {
      const layout = getAssistantAnchorLayout(anchorRef.current, placement ?? 'anchor', baseSize, offset, pageScale);
      if (!layout) return;

      setAssistantSize(layout.size);
      setAssistantPosition(position =>
        pinned && position ? clampAssistantPosition(position, layout.size.width, layout.size.height) : layout.position
      );
    };

    syncAssistantPosition();
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      syncAssistantPosition();
      secondFrame = window.requestAnimationFrame(syncAssistantPosition);
    });

    const resizeObserver = new ResizeObserver(syncAssistantPosition);
    if (anchorRef.current) {
      resizeObserver.observe(anchorRef.current);
      if (anchorRef.current.parentElement) {
        resizeObserver.observe(anchorRef.current.parentElement);
      }
    }
    window.addEventListener('resize', syncAssistantPosition);
    document.addEventListener('fullscreenchange', syncAssistantPosition);

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncAssistantPosition);
      document.removeEventListener('fullscreenchange', syncAssistantPosition);
    };
  }, [anchorRef, baseSize, enabled, offset, pageScale, pinned, placement]);

  return {
    assistantPosition,
    assistantSize,
    setAssistantPosition,
    setAssistantSize,
  };
};
